import moment from 'moment';
import { PureComponent, ReactNode } from 'react';
import { connect } from 'react-redux';

import { ThunkDispatch } from 'redux-thunk';
import fetchReceiptsByPatientId, {
    ResponseEntity,
    sendReceiptWAMessage,
} from '../api/fetchReceiptsByPatientId';
import { Actions } from '../store/actions';
import { GlobalState } from '../store/reducers';
import { getDoc } from '../store/idbHandler';
import { getAllPtIds } from './utils';
import { Response } from '../api/fetchReceiptsByPatientId';

interface Props {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
    plId: string;
    receiptId?: number;
    goToReceipt: (id?: number) => void;
}

type State = {
    fetchState: 'loading' | 'success' | 'error';
    sendAgainState: 'loading' | 'success' | 'error' | '';
    list: ResponseEntity[];
    isPreviewPrintIframeLoading: boolean;
};

export const PX_PDF_ELEMENT_ID = 'PX_PDF_ELEMENT_ID';
export const RIGHT_PANEL_CONC = 'RIGHT_PANEL_CONC';

const fetchAllReceipts = async (ptId: string): Promise<Response> => {
    const data = await getAllPtIds(ptId);

    const allPtIds = data?.allPtIds;

    if (!data || !allPtIds) {
        return {
            success: false,
        };
    }
    const allFetch = allPtIds.map((ptId) =>
        fetchReceiptsByPatientId(ptId),
    );

    try {
        const allRes = await Promise.allSettled(allFetch);
        const receipts: ResponseEntity[][] = allRes
            .map((res) => {
                if (res.status === 'fulfilled') return res.value.data;
                else return undefined;
            })
            .filter((res): res is ResponseEntity[] => res !== undefined);

        const allReceipts = ([] as ResponseEntity[])
            .concat(...receipts)
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

        const isAllPromisesFailed = allRes.every((p) => p.status === 'rejected');

        return {
            success: !isAllPromisesFailed,
            data: allReceipts,
        };
    } catch (e) {
        console.log(e);
        return {
            success: false,
        };
    }
};

class ReceiptsRightPanel extends PureComponent<Props, State> {
    state: State = {
        fetchState: 'loading',
        list: [],
        isPreviewPrintIframeLoading: false,
        sendAgainState: '',
    };

    componentDidMount = async (): Promise<void> => {
        this.fetchReceipts();
    };

    fetchReceipts = () => {
        fetchAllReceipts(this.props.plId)
            .then(async (res) => {
                if (!res.success) {
                    console.error('No data found');
                    alert('Failed to fetch latest details of the patient  [8]');
                    return;
                }

                this.setState({
                    list: res.data || [],
                    fetchState: 'success',
                });

                if (res.data && res.data.length > 0) {
                    if (!this.props.receiptId) {
                        this.props.goToReceipt(res.data[0].receipt_id);
                    } else {
                        const selectedReceipt = res.data.find(
                            (r) => r.receipt_id === this.props.receiptId,
                        );

                        if (!selectedReceipt) {
                            this.props.goToReceipt(res.data[0].receipt_id);
                        }
                    }
                } else {
                    this.props.goToReceipt();
                }
            })
            .catch((e) => {
                console.error(e);
                alert('Failed to fetch latest details of the patient  [9]');
            });
    };

    onReceiptSelect = (id: number): void => {
        this.props.goToReceipt(id);
        this.setState({
            sendAgainState: '',
        });
    };

    sendAgain = (): void => {
        const selectedReceipt = this.state.list.find((r) => r.receipt_id === this.props.receiptId);

        if (!selectedReceipt) {
            return;
        }

        this.setState({
            sendAgainState: 'loading',
        });

        sendReceiptWAMessage(getDoc()?._id || '', selectedReceipt.receipt_id)
            .then((res) => {
                if (res.success) {
                    this.setState({
                        sendAgainState: 'success',
                    });
                }
            })
            .catch((e) => {
                console.error(e);
                alert('Failed to send SMS/WhatsApp');
            });
    };

    onPrint = async () => {
        const selectedReceipt = this.state.list.find((r) => r.receipt_id === this.props.receiptId);

        if (!selectedReceipt) {
            return;
        }

        const url = selectedReceipt.receipt_url;
        window.open(url);
    };

    render(): ReactNode {
        const { list, isPreviewPrintIframeLoading, fetchState, sendAgainState } = this.state;

        const { receiptId } = this.props;

        const selectedReceipt = list.find((r) => r.receipt_id === receiptId);

        if (fetchState === 'loading') {
            return (
                <div className="flex flex-col col-span-10 items-center justify-center w-full h-full space-y-64">
                    <div className="pulse">
                        <svg width="16px" height="12px">
                            <polyline id="back" points="1 6 4 6 6 11 10 1 12 6 15 6"></polyline>
                            <polyline id="front" points="1 6 4 6 6 11 10 1 12 6 15 6"></polyline>
                        </svg>
                    </div>
                    <p className="text-24 text-neutral-black">Fetching receipts...</p>
                </div>
            );
        }

        return (
            <div className="flex col-span-10 overflow-hidden bg-white">
                <div style={{ width: 0, height: 0 }} id={RIGHT_PANEL_CONC}></div>
                <div className="w-30p">
                    <div className="flex flex-col  h-full w-full bg-neutral-9 relative fade-visits">
                        <div className="absolute inset-0 overflow-auto py-16">
                            <div className="space-y-24 p-16 ">
                                {list.length === 0 && (
                                    <div className="space-y-16">
                                        <p className="leading-5 text-12 text-neutral-5 font-700">
                                            {moment().format('MMMM YYYY')}
                                        </p>
                                        <div className="flex flex-col space-y-16">
                                            {Array.from(Array(2)).map((i) => (
                                                <div
                                                    key={i}
                                                    className="flex items-center w-full space-x-16"
                                                >
                                                    <div className="w-20 h-32 text-center font-700 bg-neutral-7"></div>
                                                    <div className="flex items-center justify-between h-32 p-8 leading-6 bg-neutral-7 w-87p rounded-32"></div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {list?.map((item, i) => (
                                    <div key={item.receipt_id} className="space-y-16">
                                        {(i === 0 ||
                                            moment(item.created_at).format('MMMM YYYY') !==
                                                moment(list[i - 1].created_at).format(
                                                    'MMMM YYYY',
                                                )) && (
                                            <p className="leading-5 text-12 text-neutral-5 font-700">
                                                {moment(item.created_at).format('MMMM YYYY')}
                                            </p>
                                        )}
                                        <div className="flex flex-col space-y-16">
                                            <div
                                                onClick={() =>
                                                    this.onReceiptSelect(item.receipt_id)
                                                }
                                                className="flex items-center space-x-16 cursor-pointer w-full"
                                            >
                                                <div className="text-center font-700">
                                                    <p
                                                        className={
                                                            receiptId !== item.receipt_id
                                                                ? 'text-18 text-neutral-5'
                                                                : 'text-18 text-neutral-1'
                                                        }
                                                    >
                                                        {new Date(item.created_at).getDate()}
                                                    </p>
                                                    <p className="text-10 text-neutral-5">
                                                        {moment(item.created_at).format('ddd')}
                                                    </p>
                                                </div>
                                                <div
                                                    className={
                                                        receiptId === item.receipt_id
                                                            ? 'flex items-center justify-between w-87p p-8 leading-6 bg-white rounded-32 shadow-tight-3 transform-gpu scale-103'
                                                            : 'flex items-center justify-between w-87p p-8 leading-6 bg-white rounded-32 patient-details-visit-shadow hover:shadow-tight-3 hover:scale-103 transition-all transform-gpu duration-300 ease-in-out'
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            'flex items-center  w-80p capitalize'
                                                        }
                                                    >
                                                        <div
                                                            className={
                                                                receiptId === item.receipt_id
                                                                    ? 'ml-8 text-13 text-neutral-1 font-700 w-85p truncate'
                                                                    : 'ml-8 text-13 text-neutral-5 w-85p truncate'
                                                            }
                                                        >
                                                            {item.net_amount
                                                                ? `₹ ${item.net_amount?.toLocaleString(
                                                                      'en-IN',
                                                                  )}`
                                                                : '-'}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <svg
                                                            width="25"
                                                            height="25"
                                                            viewBox="0 0 28 28"
                                                            className={
                                                                receiptId !== item.receipt_id
                                                                    ? 'fill-neutral-1 cursor-pointer fill-neutral-1'
                                                                    : 'fill-neutral-5 cursor-pointer fill-neutral-5'
                                                            }
                                                            preserveAspectRatio="xMidYMid meet"
                                                        >
                                                            <path d="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z"></path>
                                                        </svg>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                {list.length === 0 ? (
                    <div className="flex flex-1 flex-col items-center justify-center h-full space-y-16 select-none">
                        <div>
                            <svg
                                viewBox="0 0 448 512"
                                width="42"
                                height="48"
                                className="fill-neutral-3"
                            >
                                <path d="M400 64h-48V12c0-6.6-5.4-12-12-12h-8c-6.6 0-12 5.4-12 12v52H128V12c0-6.6-5.4-12-12-12h-8c-6.6 0-12 5.4-12 12v52H48C21.5 64 0 85.5 0 112v352c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V112c0-26.5-21.5-48-48-48zM48 96h352c8.8 0 16 7.2 16 16v48H32v-48c0-8.8 7.2-16 16-16zm352 384H48c-8.8 0-16-7.2-16-16V192h384v272c0 8.8-7.2 16-16 16zm-105.3-95.9c4.7 4.7 4.7 12.3 0 17l-5.7 5.7c-4.7 4.7-12.3 4.7-17 0l-48-48.2-48.1 48.1c-4.7 4.7-12.3 4.7-17 0l-5.7-5.7c-4.7-4.7-4.7-12.3 0-17l48.1-48.1-48.1-48.1c-4.7-4.7-4.7-12.3 0-17l5.7-5.7c4.7-4.7 12.3-4.7 17 0l48.1 48.1 48.1-48.1c4.7-4.7 12.3-4.7 17 0l5.7 5.7c4.7 4.7 4.7 12.3 0 17L246.6 336l48.1 48.1z"></path>
                            </svg>
                        </div>
                        <div>
                            <p className="text-neutral-3">No receipts for this patient yet</p>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col bg-white w-70p patient-details-viewport-shadow h-full overflow-hidden">
                        <div className="flex flex-col h-full overflow-hidden">
                            <div className="flex flex-col flex-1 overflow-hidden">
                                {selectedReceipt && (
                                    <iframe
                                        id={PX_PDF_ELEMENT_ID}
                                        className="w-full h-full fade-in"
                                        src={`/pdfjs-3/web/viewer.html?file=${
                                            selectedReceipt?.receipt_url
                                        }?t=${new Date().getTime()}`}
                                    ></iframe>
                                )}
                            </div>
                            {selectedReceipt && (
                                <div
                                    key={selectedReceipt.receipt_id}
                                    className="grid grid-rows-2 xl:grid-rows-1 gap-16 p-16 grid-flow-col  justify-between patient-details-visit-footer"
                                >
                                    <div className="w-full">
                                        <button
                                            onClick={this.onPrint}
                                            className="secondary-btn justify-center btn px-12 py-8 btn--small w-full"
                                        >
                                            <span className="pr-8 ">
                                                <svg
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 28 28"
                                                    className="fill-actionPrimary relative top-2 mr-4 cursor-pointer"
                                                    preserveAspectRatio="xMidYMid meet"
                                                >
                                                    <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z"></path>
                                                </svg>
                                            </span>
                                            <span className="whitespace-nowrap text-center">
                                                Download
                                            </span>
                                        </button>
                                    </div>

                                    <div className="flex space-x-8">
                                        <div className="w-full">
                                            <button
                                                onClick={this.sendAgain}
                                                className="secondary-btn flex justify-center btn px-12 py-8 btn--small w-full items-center space-x-4"
                                            >
                                                {sendAgainState === 'loading' ? (
                                                    <svg
                                                        viewBox="0 0 512 512"
                                                        className="fill-actionPrimary animate-spin"
                                                        width="20"
                                                        height="20"
                                                    >
                                                        <path d="M456.433 371.72l-27.79-16.045c-7.192-4.152-10.052-13.136-6.487-20.636 25.82-54.328 23.566-118.602-6.768-171.03-30.265-52.529-84.802-86.621-144.76-91.424C262.35 71.922 256 64.953 256 56.649V24.56c0-9.31 7.916-16.609 17.204-15.96 81.795 5.717 156.412 51.902 197.611 123.408 41.301 71.385 43.99 159.096 8.042 232.792-4.082 8.369-14.361 11.575-22.424 6.92z" />
                                                    </svg>
                                                ) : sendAgainState === 'success' ? (
                                                    <svg
                                                        width="20"
                                                        height="20"
                                                        viewBox="0 0 24 24"
                                                        className="fill-status-success"
                                                    >
                                                        <path d="M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z"></path>
                                                    </svg>
                                                ) : (
                                                    <svg
                                                        width="20"
                                                        height="20"
                                                        viewBox="0 0 28 28"
                                                        className="fill-actionPrimary cursor-pointer fill-actionPrimary"
                                                        preserveAspectRatio="xMidYMid meet"
                                                    >
                                                        <path d="M2,21L23,12L2,3V10L17,12L2,14V21Z"></path>
                                                    </svg>
                                                )}
                                                <span className="whitespace-nowrap text-center">
                                                    {sendAgainState === 'success'
                                                        ? 'Sent'
                                                        : 'Send SMS/WhatsApp'}
                                                </span>
                                            </button>
                                        </div>

                                        <div className="w-full">
                                            <button
                                                onClick={this.onPrint}
                                                className="primary-btn btn px-12 py-8 btn--small w-full justify-center"
                                            >
                                                <span className="pr-8 ">
                                                    {isPreviewPrintIframeLoading ? (
                                                        <svg
                                                            viewBox="0 0 512 512"
                                                            className="fill-white animate-spin"
                                                            width="20"
                                                            height="20"
                                                        >
                                                            <path d="M456.433 371.72l-27.79-16.045c-7.192-4.152-10.052-13.136-6.487-20.636 25.82-54.328 23.566-118.602-6.768-171.03-30.265-52.529-84.802-86.621-144.76-91.424C262.35 71.922 256 64.953 256 56.649V24.56c0-9.31 7.916-16.609 17.204-15.96 81.795 5.717 156.412 51.902 197.611 123.408 41.301 71.385 43.99 159.096 8.042 232.792-4.082 8.369-14.361 11.575-22.424 6.92z" />
                                                        </svg>
                                                    ) : (
                                                        <svg
                                                            width="20"
                                                            height="20"
                                                            viewBox="0 0 28 28"
                                                            className="cursor-pointer fill-white"
                                                            preserveAspectRatio="xMidYMid meet"
                                                        >
                                                            <path d="M18,3H6V7H18M19,12A1,1 0 0,1 18,11A1,1 0 0,1 19,10A1,1 0 0,1 20,11A1,1 0 0,1 19,12M16,19H8V14H16M19,8H5A3,3 0 0,0 2,11V17H6V21H18V17H22V11A3,3 0 0,0 19,8Z"></path>
                                                        </svg>
                                                    )}
                                                </span>
                                                <span className=" text-center">Print</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default connect()(ReceiptsRightPanel);
